// v3 workshop, authored the way age-of-mcp writes it: local field helpers + the
// shared styleV3 part makers, instead of a flat wall of literals. AN OPEN
// MARKET SQUARE, read from above: three canopied trade stalls around an open
// square — NO roof over the square itself, only each stall's bold sloped awning
// (red / gem-teal / wheat-gold), so the awning triangle trio is the top
// signature. A tall banner pole flying trade-red and a few barrels/crates of
// stock fill the square. Ported 1:1 from age-of-mcp's v3/workshop.ts.
import { defineModel, styleV3, type Part } from "../../lib";

const { TIMBER, WOOD, WOOD_DK, ROOF_TRADE, ROOF_FOOD, GEM, box, cyl, slab, poles } = styleV3;

const BASE = 0.12;
const GOODS = [GEM, ROOF_FOOD] as const;   // wares on each counter

// One trade stall facing +z: two posts, a counter, a bold sloped awning and
// wares. `level` is the build stage — 1 posts, 2 + counter, 3 + awning+wares.
function stall(cx: number, cz: number, awning: Part["color"], level: number): Part[] {
  const out: Part[] = [];
  for (const dx of [-0.26, 0.26]) {
    out.push(cyl(0.035, 0.035, 0.62, 4, TIMBER, [cx + dx, BASE + 0.31, cz - 0.16]));
  }
  if (level >= 2) {
    out.push(box(0.6, 0.1, 0.36, WOOD, [cx, BASE + 0.25, cz + 0.05]));
  }
  if (level >= 3) {
    out.push(box(0.72, 0.05, 0.55, awning, [cx, BASE + 0.66, cz], [0.3, 0, 0]));
    for (let i = 0; i < 2; i++) {
      out.push(box(0.16, 0.13, 0.16, GOODS[i]!, [cx - 0.14 + i * 0.28, BASE + 0.37, cz + 0.05]));
    }
  }
  return out;
}

// The three stalls around the square, awnings in three bold colours.
function stalls(level: number): Part[] {
  return [
    ...stall(-0.85, -0.6, ROOF_TRADE, level),
    ...stall(0.85, -0.55, GEM,        level),
    ...stall(-0.05, 0.85, ROOF_FOOD,  level),
  ];
}

function partsFor(s: number): Part[] {
  if (s <= 0) return [slab(2.94, 2.94)];
  if (s === 1) return [slab(2.94, 2.94), ...stalls(1), ...poles(2.8, 2.8, 0.8)];
  if (s === 2) return [slab(2.94, 2.94), ...stalls(2), ...poles(2.8, 2.8, 1.0)];

  return [
    slab(2.94, 2.94),
    ...stalls(3),
    // The banner pole marking the square, flying trade red.
    cyl(0.03, 0.04, 1.35, 5, TIMBER, [0.75, BASE + 0.675, 0.15]),
    box(0.34, 0.2, 0.03, ROOF_TRADE, [0.93, BASE + 1.18, 0.15]),
    // Stock standing about the open square.
    cyl(0.15, 0.14, 0.32, 8, WOOD_DK, [0.55, BASE + 0.16, 0.45]),
    box(0.28, 0.28, 0.28, WOOD_DK, [-0.7, BASE + 0.14, 0.25]),
  ];
}

export const workshopV3 = defineModel({
  id: "workshop-v3",
  tiles: { w: 3, h: 3 },
  batchId: "45-optimized",
  name: "Workshop (v3)",
  tags: ["building","workshop","low-poly","age-of-mcp","v3"],
  summary: "Low-poly workshop.",
  description: "A walled crafting compound of benches under a tiled roof.",
  author: "age-of-mcp",
  footprint: 1.5,
  defaultVariant: "complete",
  variants: [
    { id: "stage-0", label: "Foundation", parts: () => partsFor(0) },
    { id: "stage-1", label: "Lower", parts: () => partsFor(1) },
    { id: "stage-2", label: "Upper", parts: () => partsFor(2) },
    { id: "complete", label: "Complete", parts: () => partsFor(3) },
  ],
  animations: [],
});
